<template>
  <div v-if="!isLoading" class="change-my-pro">
    <div v-if="!hasEligiblePros" class="no-chengeable-pros">
      <LsInfoCard v-if="hasDisputedPros">
        <h4>Cannot change pro</h4>
        <p>
          It looks like you requested to have your pro return to fix an issue with your service.
          While that request is in progress, we are unable to change your pro.
        </p>
      </LsInfoCard>
      <LsInfoCard v-if="hasOneTimeDayChangePros">
        <h4>Cannot change pro</h4>
        <p>
          It looks like you already requested a one-time reschedule for this service. While this
          request is in progress, we are unable to change your pro.
        </p>
      </LsInfoCard>
      <LsInfoCard v-if="hasDistributingServices">
        <h4>Cannot change pro</h4>
        <p>
          We are already working on finding a new pro to service your lawn care needs. While this
          change is in progress, we are unable to accept a request to change pros. You will be
          notified when a new pro is assigned.
        </p>
      </LsInfoCard>
      <LsInfoCard v-if="!hasProviders">
        <h4>Cannot change pro</h4>
        <p>
          It looks like you do not currently have any active services, so we are unable to accept a
          request to change pros. You can return here to manage your pro once your service is
          active.
        </p>
      </LsInfoCard>
      <LsButton
        class="back"
        text="back"
        track-id="change_my_pro-ineligible_back"
        bg-color="primary"
        @click="$router.push({ name: 'support' })"
      />
    </div>
    <div v-else class="has-chengeable-pros">
      <LsInfoCard>
        <h4>What to expect</h4>
        <p>
          We will do our best to keep your service on the same two-day schedule, but cannot make any
          guarantees.
          <LsClickable track-id="change_my_pro-learn_more">
            <a href="#" @click="learnMore">Learn more</a>
          </LsClickable>
        </p>
      </LsInfoCard>
      <p>Please complete the form below and we'll set you up with a different pro.</p>
      <form>
        <div class="ctrl-group">
          <LsSelect
            v-model="form.property"
            label="Property to change pro"
            track-id="change_my_pro-property_select"
            :options="propertiesList"
            name="property-select"
            placeholder="-Select-"
            :validation="{ required: true }"
          />
        </div>
        <div v-if="form.property && pros.length > 0" class="ctrl-group">
          <LsSelect
            v-model="form.reason"
            label="Reason for changing pro?"
            track-id="change_my_pro-reason_select"
            :options="reasons"
            name="reason-select"
            placeholder="-Select-"
            :validation="{ required: true }"
          />
        </div>
        <div v-if="form.reason === 'customer.other'" class="ctrl-group">
          <LsInput
            v-model="form.reasonOther"
            label="Specify change pro reason"
            track-id="change_my_pro-reason_other"
            name="reason-other"
            placeholder="Enter details here..."
            type="textarea"
            :validation="{ required: true }"
            has-counter
            maxlength="512"
          />
        </div>
        <div v-if="pros.length > 0 && form.reason" class="ctrl-group">
          <LsRadios
            v-model="form.pro"
            label="Choose the pro to change"
            :options="pros"
            name="providers"
            track-id="change_my_pro-provider"
            :item-component="ProviderLabel"
            :validation="{ required: true }"
            raw-style
          />
        </div>
        <div class="ctrl-group compensate-icon">
          <LsInfoCard v-if="form.property && pros.length === 0">
            <h4>Cannot change pro</h4>
            <p>
              It looks like you do not currently have any active services, so we are unable to
              accept a request to change pros. You can return here to manage your pro once your
              service is active.
            </p>
          </LsInfoCard>

          <LsInfoCard v-if="currentPro && currentPro.hasServiceWithinNext48h">
            <h4>We'll change your pro after your next service</h4>
            <p>
              Given it can take some time to swap out your pro, we request a 48 hour notice to make
              this change. If your next service is scheduled within that window your original pro
              will come by to complete the job, and we'll make the change for all following
              services.
            </p>
          </LsInfoCard>

          <div
            v-if="isCurrentReasonFixable && currentPro && currentPro.lastServiceWithin5daysAgo"
            class="v-stack"
          >
            <LsInfoCard
              :class="{
                'no-margin-top': currentPro && currentPro.hasServiceWithinNext48h,
              }"
            >
              <h4>Are you 100% sure?</h4>
              <p>You can request the same pro to come back and correct/complete the work.</p>
            </LsInfoCard>
            <LsButton
              text="let my pro fix the issue"
              track-id="change_my_pro-let_my_pro_fix_the_issue"
              bg-color="primary"
              @click="onLetMyProFix"
            />
            <LsButton
              text="change my pro"
              track-id="change_my_pro-change_my_pro"
              bg-color="primary-light"
              :loading="isUpdating"
              @click="onSubmit"
            />
          </div>

          <LsButton
            v-else-if="pros.length > 0"
            class="submit"
            text="submit request"
            track-id="change_my_pro-submit"
            bg-color="primary"
            :disabled="!form.pro"
            :loading="isUpdating"
            @click="onSubmit"
          />
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { DisputeMowModal } from '@/modals';
import { mapActions, mapState } from 'vuex';
import { lsModalService } from 'ls-vue-common';
import { changeMyProReasons } from 'ls-constants';
import ProviderRadioLabel from './components/ProviderRadioLabel.vue';
import {
  PROPERTIES__GET_ELIGIBLE_PROS_FOR_REDISTRIBUTION,
  PROPERTIES__REDISTRIBUTE_PRO,
} from '@/store/types';
import {
  LsClickable,
  LsSelect,
  LsButton,
  LsInfoCard,
  LsInput,
  LsRadios,
} from '@/components/common';

export default {
  name: 'ChangeMyPro',
  $_veeValidate: {
    validator: 'new',
  },
  components: { LsClickable, LsSelect, LsButton, LsInfoCard, LsInput, LsRadios },
  data() {
    return {
      pros: [],
      reasons: changeMyProReasons,
      ProviderLabel: ProviderRadioLabel,
      form: {
        property: null,
        reason: null,
        reasonOther: '',
        pro: null,
      },
    };
  },
  computed: {
    ...mapState(['properties']),
    hasEligiblePros() {
      return this.properties.eligibleProsForRedistribution.hasEligiblePros;
    },
    hasDisputedPros() {
      return this.properties.eligibleProsForRedistribution.hasDisputedPros;
    },
    hasOneTimeDayChangePros() {
      return this.properties.eligibleProsForRedistribution.hasOneTimeDayChangePros;
    },
    hasDistributingServices() {
      return this.properties.eligibleProsForRedistribution.hasDistributingServices;
    },
    isLoading() {
      return this.properties.loadingStatus.isLoading;
    },
    isUpdating() {
      return this.properties.loadingStatus.isUpdating;
    },
    propertiesList() {
      return this.properties.eligibleProsForRedistribution.properties.map(
        ({ street: label, id: value }) => ({ label, value })
      );
    },
    currentProperty() {
      return this.properties.eligibleProsForRedistribution.properties.find(
        ({ id }) => id === this.form.property
      );
    },
    currentPro() {
      if (!this.currentProperty || !this.currentProperty.providers) {
        return null;
      }

      return this.currentProperty.providers.find(({ id }) => id === this.form.pro);
    },
    isCurrentReasonFixable() {
      return (
        this.form.reason === 'customer.job.incomplete' ||
        this.form.reason === 'customer.quality.issue'
      );
    },
    hasProviders() {
      return !!this.properties.eligibleProsForRedistribution.properties.reduce(
        (acc, { providers }) => {
          return [...acc, ...providers];
        },
        []
      ).length;
    },
  },
  watch: {
    propertiesList() {
      if (this.propertiesList.length === 1) {
        this.form.property = this.propertiesList[0].value;
      }
    },
    currentProperty(property) {
      const providersList = [...(property.providers || [])];

      // property change should reset selected pro
      this.form.pro = null;

      this.pros = providersList
        .filter(({ id }) => !!id)
        .sort(this.sortPros)
        .map(
          ({
            id,
            company,
            name,
            services,
            hasDispute,
            hasOneTimeDayChange,
            hasDistributingServices,
          }) => ({
            value: id,
            boldLabel: true,
            subLabel: services.join(', '),
            label: `${company} (${name})`,
            disabled: hasDispute || hasOneTimeDayChange || hasDistributingServices,
            bundle: {
              isBlockedByReturnRequest: hasDispute,
              isBlockedByOneTimeReschedule: hasOneTimeDayChange,
              isBlockedByDistribution: hasDistributingServices,
            },
          })
        );
    },
  },
  async beforeMount() {
    await this.getEligiblePros();
  },
  methods: {
    ...mapActions({
      getEligiblePros: PROPERTIES__GET_ELIGIBLE_PROS_FOR_REDISTRIBUTION,
      redistribute: PROPERTIES__REDISTRIBUTE_PRO,
    }),
    learnMore(event) {
      event.preventDefault();
      this.$router.push({ name: 'LearnMore' });
    },
    sortPros(prev, next) {
      const prevIsBlocked =
        prev.hasDispute || prev.hasOneTimeDayChange || prev.hasDistributingServices;
      const nextIsBlocked =
        next.hasDispute || next.hasOneTimeDayChange || next.hasDistributingServices;

      if (prevIsBlocked && nextIsBlocked) {
        return 0;
      } else if (prevIsBlocked && !nextIsBlocked) {
        return 1;
      }

      return -1;
    },
    async onSubmit() {
      const isValid = this.form.pro && (await this.$validator.validateAll());

      if (isValid) {
        const isSuccessful = await this.redistribute(this.form);

        if (isSuccessful) {
          this.$router.push({ name: 'ChangeProSuccess' });
        } else {
          this.$router.push({ name: 'ChangeProFail' });
        }
      }
    },
    async onLetMyProFix(event) {
      event.preventDefault();
      const isValid = this.form.pro && (await this.$validator.validateAll());

      if (isValid) {
        lsModalService.open({
          parent: this,
          component: DisputeMowModal,
          props: {
            targetProperty: this.form.property,
          },
        });
      }
    },
  },
};
</script>

<style lang="scss">
@media (min-width: $desktop) {
  .change-my-pro {
    form {
      .ctrl-group {
        &.compensate-icon {
          padding: 1rem 0 0 !important;
        }
      }
    }
  }
}

.change-my-pro {
  .v-stack {
    display: flex;
    flex-flow: column;

    button {
      margin-bottom: 1.25rem;
    }
  }
  form {
    .ctrl-group {
      padding: 1.5rem 0 0;

      &:first-child {
        padding: 1rem 0 0;
      }

      &.compensate-icon {
        padding: 3rem 0 0;
      }
    }
  }
  .info-card {
    margin: 1rem 0 3rem 0;
    flex-direction: column;
    align-items: flex-start;
    padding: 2rem 2rem 2rem 4rem;

    &.no-margin-top {
      margin-top: 0 !important;
    }

    h4 {
      font-weight: 700;
      margin-bottom: 2px;
      text-transform: uppercase;
    }
  }
  .submit {
    margin-top: 2rem;
  }
}
</style>
